<script setup>

import {nextTick} from 'vue';

import SearchPill from "@/components/common/other/SearchPill";
import API from "@/classes/global/API";
import DropDownItem from "@/classes/component-classes/DropDownItem";
import MultiSearchDropDown from "@/components/common/input/MultiSearchDropDown";
import StockSearchItem from "@/classes/stock/StockSearchItem";
import StaticTooltip from "@/components/common/other/StaticTooltip";

import {useRoute} from "vue-router";
import {useCheckFieldsForData} from "@/composables/common/useCheckFieldsForData";
import {useCheckSearchCriteriaExists} from "@/composables/common/useCheckSearchCriteriaExists";

const route = useRoute();
const emits = defineEmits(['search', 'clear']);
const props = defineProps({modelValue:{}, url: String});

function handleSearch(searchPill) {

  nextTick(async () => {

    props.modelValue.isSearchData = useCheckSearchCriteriaExists(props.modelValue.searchCriteria);

    if (props.modelValue.isSearchData) {

      emits('search');

      // clearGridLineData();

      const response = await API.searchStock(props.modelValue.searchCriteria, props.url);

      if (response) {

        props.modelValue.searchData = response;

        let searchHistoryItemExists;

        if (props.modelValue.searchCriteria[searchPill]) {

          searchHistoryItemExists = props.modelValue.searchPills[searchPill]
              .history?.find((item) => item.value === props.modelValue.searchCriteria[searchPill]);

          if (!searchHistoryItemExists && response.length > 0) props.modelValue.searchPills[searchPill].history
              ?.unshift(new DropDownItem(props.modelValue.searchPills[searchPill].history.length + 1,
                  props.modelValue.searchCriteria[searchPill]));
        }

        if (response.length === 0) props.modelValue.isSearchData = false;
      }

      return

    }

    props.modelValue.searchData = [];

  })

}

function handleClearSearch(searchPill) {

  // Move the events along to ensure clearing of the box has happened before data present check
  nextTick(() => {

    if (!useCheckFieldsForData(props.modelValue.searchCriteria)) {
      handleClearAllFields();

    } else {

      handleSearch(searchPill);
    }

  })

}

function handleClearAllFields() {

  props.modelValue.searchCriteria = new StockSearchItem();
  emits('clear');

}

function toggleSearchGroup(button) {

  if(button && props.modelValue.searchGroup) return;
  if(!button && !props.modelValue.searchGroup) return;

  props.modelValue.searchGroup = !props.modelValue.searchGroup;
}
</script>

<template>
  <div class="cmp-search-stock-pill-group_search-area">
    <div v-if="props.modelValue.searchGroup" class="cmp-search-stock-pill-group_search-pill-container">
      <SearchPill
          @search="handleSearch('skuNumber')"
          :enabled="props.modelValue.searchPills.skuNumber.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="278"
              :maxLength="50"
              placeholder=""
              :allowCustomValue="true"
              :disableFilter="false"
              searchType="iata"
              label="SKU Number"
              :label-top="true"
              @clear="handleClearSearch('skuNumber')"
              @select="handleSearch('skuNumber')"
              @keydown.enter="handleSearch('skuNumber')"
              :dataSource="props.modelValue.searchPills.skuNumber.history"
              v-model="props.modelValue.searchCriteria.skuNumber"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('skuName')"
          :enabled="props.modelValue.searchPills.skuName.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="280"
              :maxLength="50"
              placeholder=""
              :allowCustomValue="true"
              :disableFilter="false"
              searchType="iata"
              label="SKU Name"
              :label-top="true"
              @clear="handleClearSearch('skuName')"
              @select="handleSearch('skuName')"
              @keydown.enter="handleSearch('skuName')"
              :dataSource="props.modelValue.searchPills.skuName.history"
              v-model="props.modelValue.searchCriteria.skuName"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('skuDescription')"
          :enabled="props.modelValue.searchPills.skuDescription.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="400"
              :maxLength="50"
              placeholder=""
              :allowCustomValue="true"
              :disableFilter="false"
              searchType="iata"
              label="Description"
              :label-top="true"
              @clear="handleClearSearch('skuDescription')"
              @select="handleSearch('skuDescription')"
              @keydown.enter="handleSearch('skuDescription')"
              :dataSource="props.modelValue.searchPills.skuDescription.history"
              v-model="props.modelValue.searchCriteria.skuDescription"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('warehouse')"
          :enabled="props.modelValue.searchPills.warehouse.enabled">
        <template #control>
          <MultiSearchDropDown
              :key="props.modelValue.metaData.warehouses"
              :width="280"
              :disableFilter="true"
              placeholder="Select...."
              :selectPrimitiveValue="true"
              :label-top="true"
              @clear="handleClearSearch('warehouse')"
              @select="handleSearch('warehouse')"
              @keydown.enter="handleSearch('warehouse')"
              v-model="props.modelValue.searchCriteria.warehouse"
              :dataSource="props.modelValue.metaData.warehouses"
              label="Warehouse">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
    </div>
    <div v-else class="cmp-search-stock-pill-group_search-pill-container">
      <SearchPill
          @search="handleSearch('descHeadOne')"
          :enabled="props.modelValue.searchPills.descHeadOne.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="180"
              :maxLength="50"
              placeholder=""
              :allowCustomValue="true"
              :disableFilter="false"
              searchType="iata"
              :label="props.modelValue.searchPills.descHeadOne.label"
              :label-top="true"
              @clear="handleClearSearch('descHeadOne')"
              @select="handleSearch('descHeadOne')"
              @keydown.enter="handleSearch('descHeadOne')"
              :dataSource="props.modelValue.searchPills.descHeadOne.history"
              v-model="props.modelValue.searchCriteria.descHeadOne"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('descHeadTwo')"
          :enabled="props.modelValue.searchPills.descHeadTwo.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="180"
              :maxLength="50"
              placeholder=""
              :allowCustomValue="true"
              :disableFilter="false"
              searchType="iata"
              :label="props.modelValue.searchPills.descHeadTwo.label"
              :label-top="true"
              @clear="handleClearSearch('descHeadTwo')"
              @select="handleSearch('descHeadTwo')"
              @keydown.enter="handleSearch('descHeadTwo')"
              :dataSource="props.modelValue.searchPills.descHeadTwo.history"
              v-model="props.modelValue.searchCriteria.descHeadTwo"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('descHeadThree')"
          :enabled="props.modelValue.searchPills.descHeadThree.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="180"
              :maxLength="50"
              placeholder=""
              :allowCustomValue="true"
              :disableFilter="false"
              searchType="iata"
              :label="props.modelValue.searchPills.descHeadThree.label"
              :label-top="true"
              @clear="handleClearSearch('descHeadThree')"
              @select="handleSearch('descHeadThree')"
              @keydown.enter="handleSearch('descHeadThree')"
              :dataSource="props.modelValue.searchPills.descHeadThree.history"
              v-model="props.modelValue.searchCriteria.descHeadThree"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('descHeadFour')"
          :enabled="props.modelValue.searchPills.descHeadFour.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="180"
              :maxLength="50"
              placeholder=""
              :allowCustomValue="true"
              :disableFilter="false"
              searchType="iata"
              :label="props.modelValue.searchPills.descHeadFour.label"
              :label-top="true"
              @clear="handleClearSearch('descHeadFour')"
              @select="handleSearch('descHeadFour')"
              @keydown.enter="handleSearch('descHeadFour')"
              :dataSource="props.modelValue.searchPills.descHeadFour.history"
              v-model="props.modelValue.searchCriteria.descHeadFour"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('descHeadFive')"
          :enabled="props.modelValue.searchPills.descHeadFive.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="180"
              :maxLength="50"
              placeholder=""
              :allowCustomValue="true"
              :disableFilter="false"
              searchType="iata"
              :label="props.modelValue.searchPills.descHeadFive.label"
              :label-top="true"
              @clear="handleClearSearch('descHeadFive')"
              @select="handleSearch('descHeadFive')"
              @keydown.enter="handleSearch('descHeadFive')"
              :dataSource="props.modelValue.searchPills.descHeadFive.history"
              v-model="props.modelValue.searchCriteria.descHeadFive"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('descHeadSix')"
          :enabled="props.modelValue.searchPills.descHeadSix.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="180"
              :maxLength="50"
              placeholder=""
              :allowCustomValue="true"
              :disableFilter="false"
              searchType="iata"
              :label="props.modelValue.searchPills.descHeadSix.label"
              :label-top="true"
              @clear="handleClearSearch('descHeadSix')"
              @select="handleSearch('descHeadSix')"
              @keydown.enter="handleSearch('descHeadSix')"
              :dataSource="props.modelValue.searchPills.descHeadSix.history"
              v-model="props.modelValue.searchCriteria.descHeadSix"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
    </div>
    <div class="cmp-search-stock-pill-group_clear-button">
      <StaticTooltip :icon="false">
        <template #content>
          Clear All
        </template>
        <i class="fa-solid fa-trash-can-list" style="color:#4c4c4c; cursor: pointer;"
           @click="handleClearAllFields"></i>
      </StaticTooltip>
    </div>
  </div>

</template>

<style lang="scss" scoped>

.cmp-search-stock-pill-group {

&_search-area {
   display: flex;
   align-items: center;
   padding-left: 10px;
   height: 90px;
   //margin-left: 10px;
   background-color: #ffffff;
   border-radius: var(--rounded-corners);
   border: 1px solid var(--border-color);
   border-left: 6px solid var(--left-border-highlight);
 }

&_search-pill-container {
   display: flex;
   align-items: center;
   height: 80px;
   gap: 20px;
   overflow-x: auto;
   overflow-y: hidden;
   padding: 0 5px 0 5px;
 }

  &_clear-button {
    display: flex;
    align-items: center;
    margin-left: 30px;
  }

}
</style>
